import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { DoctorAuthService } from '../api/AuthService';
import { AuthApiService } from '../api/AuthApiService';
import { DOCTOR_SESSION_EXPIRED_EVENT, triggerDoctorLogout } from '../../../shared/utils/events/DoctorLogoutEvent';

interface AuthContextType {
  authService: DoctorAuthService;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authService] = useState(() => new DoctorAuthService());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authApi] = useState(() => AuthApiService.getInstance());

  const checkAuth = async (): Promise<boolean> => {
    try {
      // Reload tokens from localStorage to ensure we have the latest
      authApi.loadStoredTokens();

      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (!token && !refreshToken) {
        setIsAuthenticated(false);
        return false;
      }

      // If we have tokens, consider authenticated.
      // fetchWithAuth will handle refresh lazily if the access token is expired.
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      setIsAuthenticated(false);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    console.log('🔄 AuthContext logout called');
    try {
      const result = await authService.logout();

      if (result.isSuccess) {
        console.log('✅ Logout API call successful');
      } else {
        console.log('❌ Logout API call failed:', result.error);
      }
    } catch (error) {
      console.log('❌ Network error during logout:', error);
    }

    // Clear local authentication state
    setIsAuthenticated(false);
    console.log('✅ Authentication state cleared');

    // Clear all storage data
    try {
      sessionStorage.clear();
      console.log('✅ SessionStorage cleared');

      // Clear payment-related data from localStorage
      localStorage.removeItem('paymentId');
      console.log('✅ Payment data cleared from localStorage');
    } catch (e) {
      console.log('⚠️ Error clearing storage:', e);
    }

    // Notify all contexts and components
    triggerDoctorLogout();

    console.log('✅ Complete logout cleanup finished');
  };

  // The server refused the refresh token: the tokens are already cleared, so leave the signed-in
  // screens (both designs redirect to /login when not authenticated) instead of showing empty or
  // failing pages that look like the doctor has no clinic or no requests.
  useEffect(() => {
    const onExpired = () => {
      setIsAuthenticated(false);
      triggerDoctorLogout();
    };
    window.addEventListener(DOCTOR_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(DOCTOR_SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // On mount: check auth only (data fetching moved to ProgressContext)
  useEffect(() => {
    const initialize = async () => {
      await checkAuth();
      setIsLoading(false);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: AuthContextType = {
    authService,
    isAuthenticated,
    isLoading,
    checkAuth,
    logout,
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context.authService;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}